import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, EventType } from '@azure/msal-browser';
import { loginRequest } from './authConfig.js';
import NavBar from './components/NavBar.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import Home from './pages/Home.jsx';
import Report from './pages/Report.jsx';
import AdminLayout from './pages/admin/AdminLayout.jsx';
import Users from './pages/admin/Users.jsx';
import Permissions from './pages/admin/Permissions.jsx';
import Logs from './pages/admin/Logs.jsx';

export default function App() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const [loginError, setLoginError] = useState(null);

  // Keep active account in sync
  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const doLogin = useCallback(() => {
    setLoginError(null);
    instance.loginRedirect(loginRequest).catch((err) => {
      console.error('[MSAL] loginRedirect error:', err);
      setLoginError(err.message || 'Erro de autenticação');
    });
  }, [instance]);

  // Detect MSAL auth failures (e.g. stale refresh token → 400 loop) and surface them
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_FAILURE ||
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE
      ) {
        console.error('[MSAL] auth event failure:', event.eventType, event.error);
        setLoginError(event.error?.message ?? 'Falha na autenticação. Tente novamente.');
      }
    });
    return () => { if (callbackId) instance.removeEventCallback(callbackId); };
  }, [instance]);

  // Auto-trigger login when not authenticated and no interaction in flight
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None && !loginError) {
      doLogin();
    }
  }, [isAuthenticated, inProgress, loginError, doLogin]);

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '1rem' }}>
        {loginError ? (
          <>
            <p style={{ color: 'red' }}>Erro: {loginError}</p>
            <button onClick={doLogin}>Tentar novamente</button>
          </>
        ) : (
          <p>A autenticar...</p>
        )}
      </div>
    );
  }

  return (
    <BrowserRouter>
      <NavBar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/report/:reportId" element={<Report />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<Users />} />
          <Route path="permissions" element={<Permissions />} />
          <Route path="logs" element={<Logs />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
